if (!customElements.get('hg-tea-mix')) {
  customElements.define('hg-tea-mix', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.strings = JSON.parse(this.querySelector('[data-mix-strings]').textContent);
      this.selection = new Map();
      this.variants = new Map();
      this.cards = [...this.querySelectorAll('[data-product-card]')];
      this.busy = false;
      this.uncertain = false;
      this.celebrating = 0;
      this.stockInCart = new Map();
      this.cartStockReady = false;
      this.activeFilter = 'all';
      this.prefillVariantId = null;
      this.prefillMessageKey = '';
      const prefillValues = new URL(window.location.href).searchParams.getAll('mix_variant');
      this.prefillPending = prefillValues.length > 0;
      this.cartGeneration = 0;
      this.listeners = new AbortController();
      this.storageKey = `hg-tea-mix:${this.dataset.sectionId}:${this.dataset.currency}`;
      this.formatter = new Intl.NumberFormat((this.dataset.currency || 'EUR') === 'EUR' ? 'de-DE' : this.dataset.locale || 'de-DE', { style: 'currency', currency: this.dataset.currency || 'EUR' });
      this.cards.forEach(card => {
        card.querySelectorAll('[data-variant-select] option').forEach(option => {
          this.variants.set(option.value, {
            id: option.value,
            title: card.dataset.productTitle,
            variantTitle: option.dataset.variantTitle,
            url: card.dataset.productUrl,
            image: option.dataset.image,
            imageAlt: option.dataset.imageAlt,
            price: Number(option.dataset.price),
            compare: Number(option.dataset.compare),
            unitPrice: Number(option.dataset.unitPrice),
            unitReference: option.dataset.unitReference,
            stock: Number(option.dataset.stock),
            available: option.dataset.available === 'true',
            card
          });
        });
      });
      this.restore();
      this.setFilter('all');
      this.addEventListener('click', event => this.onClick(event), { signal: this.listeners.signal });
      this.addEventListener('change', event => {
        if (event.target.matches('[data-variant-select]')) this.renderCard(event.target.closest('[data-product-card]'));
      }, { signal: this.listeners.signal });
      document.addEventListener('hg:mix-campaign-ended', () => this.render(), { signal: this.listeners.signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) return;
        this.render();
        this.scheduleOfferBoundary();
      }, { signal: this.listeners.signal });
      const api = this.themeApi;
      if (api?.pubsub?.PUB_SUB_EVENTS) {
        this.unsubscribeCart = api.pubsub.subscribe(api.pubsub.PUB_SUB_EVENTS.cartUpdate, event => {
          this.cartGeneration += 1;
          if (event.cart?.items) {
            this.stockInCart = this.cartQuantities(event.cart);
            this.cartStockReady = true;
            if (!this.busy) this.render();
          } else this.syncCartStock();
        });
      }
      this.render();
      this.scheduleOfferBoundary();
      this.observer = new IntersectionObserver(entries => {
        this.querySelector('[data-dock]').hidden = !entries[0].isIntersecting;
      });
      this.observer.observe(this);
      const connectionSignal = this.listeners.signal;
      this.syncCartStock().then(() => {
        if (!connectionSignal.aborted && this.isConnected && this.prefillPending) this.applyPrefill(prefillValues);
      });
    }

    disconnectedCallback() {
      clearTimeout(this.celebrationTimer);
      clearTimeout(this.offerBoundaryTimer);
      this.observer?.disconnect();
      this.listeners?.abort();
      this.unsubscribeCart?.();
      this.cartGeneration += 1;
      this.initialized = false;
    }

    get themeApi() { return typeof theme !== 'undefined' ? theme : null; }
    get count() { return [...this.selection.values()].reduce((sum, quantity) => sum + quantity, 0); }
    get total() { return [...this.selection].reduce((sum, [id, quantity]) => sum + this.variants.get(id).price * quantity, 0); }
    get currentTier() { return this.count >= 8 ? 8 : this.count >= 5 ? 5 : this.count >= 3 ? 3 : 0; }
    get activeRate() { return this.discountsActive && this.currentTier ? this.rates[this.currentTier] : 0; }
    discountedUnitPrice(variant) {
      // Shopify rounds each unit's percentage discount down to whole currency cents.
      return variant.price - Math.floor(variant.price * this.activeRate / 100);
    }
    get discountedTotal() { return [...this.selection].reduce((sum, [id, quantity]) => sum + this.discountedUnitPrice(this.variants.get(id)) * quantity, 0); }
    get nextTarget() { return this.count < 3 ? 3 : this.count < 5 ? 5 : 8; }
    get rates() { return { 3: Number(this.dataset['discount-3']), 5: Number(this.dataset['discount-5']), 8: Number(this.dataset['discount-8']) }; }
    get campaignEnded() {
      const expires = Date.parse(this.dataset.campaignEnds || '');
      return Number.isFinite(expires) && Date.now() >= expires;
    }
    get campaignPending() {
      const starts = Date.parse(this.dataset.campaignStarts || '');
      return Number.isFinite(starts) && Date.now() < starts;
    }
    get offerAvailable() {
      if (this.campaignEnded) return false;
      const rates = this.rates;
      return this.dataset.discountsEnabled === 'true' && rates[3] > 0 && rates[5] > rates[3] && rates[8] > rates[5] && rates[8] <= 50;
    }
    get discountsActive() { return this.offerAvailable && !this.campaignPending; }
    get cartUrl() { return this.dataset.cartUrl; }
    get cartJsonUrl() { return `${this.cartUrl.replace(/\.js$/, '')}.js`; }
    get addUrl() { return `${this.dataset.addUrl.replace(/\.js$/, '')}.js`; }
    money(cents) { return this.formatter.format(cents / 100); }
    t(key, values = {}) { return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`[${name}]`, String(value)), this.strings[key] || ''); }
    setText(selector, text) { this.querySelectorAll(selector).forEach(element => { element.textContent = text; }); }

    scheduleOfferBoundary() {
      clearTimeout(this.offerBoundaryTimer);
      if (!this.isConnected || this.listeners?.signal.aborted) return;
      const now = Date.now();
      const boundary = [this.dataset.campaignStarts, this.dataset.campaignEnds]
        .map(value => Date.parse(value || ''))
        .filter(value => Number.isFinite(value) && value > now)
        .sort((first, second) => first - second)[0];
      if (boundary === undefined) return;
      this.offerBoundaryTimer = setTimeout(() => {
        if (!this.isConnected || this.listeners?.signal.aborted) return;
        this.render();
        this.scheduleOfferBoundary();
      }, Math.min(boundary - now, 2147483647));
    }

    renderOffer() {
      const available = this.offerAvailable;
      this.querySelectorAll('[data-offer-ready]').forEach(element => { element.hidden = !available; });
      this.querySelectorAll('[data-offer-neutral]').forEach(element => { element.hidden = available; });
      this.querySelectorAll('[data-offer-upcoming]').forEach(element => { element.hidden = !available || !this.campaignPending; });
      this.querySelectorAll('[data-offer-active]').forEach(element => { element.hidden = !this.discountsActive; });
    }

    cartQuantities(cart) {
      const quantities = new Map();
      (cart.items || []).forEach(item => {
        const id = String(item.variant_id || item.id);
        quantities.set(id, (quantities.get(id) || 0) + Number(item.quantity));
      });
      return quantities;
    }

    async readCart(signal) {
      const response = await fetch(this.cartJsonUrl, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store', signal });
      if (!response.ok) throw new Error(this.t('error'));
      return response.json();
    }

    async syncCartStock() {
      const generation = ++this.cartGeneration;
      try {
        const cart = await this.readCart(this.prefillPending ? AbortSignal.timeout(10000) : undefined);
        if (generation !== this.cartGeneration || !this.isConnected) return;
        this.stockInCart = this.cartQuantities(cart);
        this.cartStockReady = true;
        if (!this.busy) this.render();
      } catch (_) {}
    }

    setFilter(value) {
      if (!['all', 'tea', 'bears'].includes(value)) return;
      this.activeFilter = value;
      const countFor = filter => this.cards.filter(card => filter === 'all' || card.dataset.mixFamily === filter).length;
      this.querySelectorAll('[data-mix-filter]').forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.mixFilter === value));
        const count = button.querySelector('[data-filter-count]');
        if (count) count.textContent = String(countFor(button.dataset.mixFilter));
      });
      this.cards.forEach(card => { card.hidden = value !== 'all' && card.dataset.mixFamily !== value; });
      const count = countFor(value);
      this.setText('[data-filter-status]', count ? this.t('filterCount', { count }) : this.t('noFilterProducts'));
      const filters = this.querySelector('[data-mix-filters]');
      if (filters) filters.hidden = false;
    }

    renderPrefillMessage() {
      if (this.prefillVariantId) {
        if (this.selection.has(this.prefillVariantId)) this.prefillMessageKey = this.count < 3 ? 'preselected' : 'preselectedReady';
        else {
          this.prefillVariantId = null;
          this.prefillMessageKey = '';
        }
      }
      const message = this.t(this.prefillMessageKey, { count: Math.max(0, 3 - this.count) });
      const element = this.querySelector('[data-prefill-message]');
      if (element) {
        element.hidden = !message;
        if (element.textContent !== message) element.textContent = message;
      }
      return message;
    }

    applyPrefill(values) {
      this.prefillPending = false;
      const id = values.length === 1 && /^[1-9]\d*$/.test(values[0]) ? values[0] : null;
      const variant = id ? this.variants.get(id) : null;
      if (!variant?.available) this.prefillMessageKey = 'preselectedUnavailable';
      else if (this.selection.has(id)) {
        this.prefillVariantId = id;
        variant.card.querySelector('[data-variant-select]').value = id;
      } else if (this.count >= 8) this.prefillMessageKey = 'preselectedFull';
      else if (!this.cartStockReady) this.prefillMessageKey = 'preselectedCartUnavailable';
      else if (this.remainingStock(variant) < 1) this.prefillMessageKey = 'preselectedUnavailable';
      else {
        this.selection.set(id, 1);
        this.prefillVariantId = id;
        variant.card.querySelector('[data-variant-select]').value = id;
        this.persist();
      }
      const url = new URL(window.location.href);
      url.searchParams.delete('mix_variant');
      try { window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`); } catch (_) {}
      this.render();
      const message = this.renderPrefillMessage();
      if (message) this.setText('[data-announcement]', message);
    }

    remainingStock(variant) {
      return variant.stock < 0 ? Infinity : Math.max(0, variant.stock - (this.stockInCart.get(variant.id) || 0));
    }

    restore() {
      try {
        const stored = JSON.parse(sessionStorage.getItem(this.storageKey) || '[]');
        if (!Array.isArray(stored)) return;
        stored.forEach(entry => {
          if (!Array.isArray(entry)) return;
          const [id, quantity] = entry;
          const variant = this.variants.get(String(id));
          if (!variant?.available || !Number.isSafeInteger(quantity) || quantity < 1) return;
          const permitted = Math.min(quantity, 8 - this.count, variant.stock < 0 ? 8 : variant.stock);
          if (permitted > 0) this.selection.set(String(id), permitted);
        });
      } catch (_) {}
    }

    persist() {
      try { sessionStorage.setItem(this.storageKey, JSON.stringify([...this.selection])); } catch (_) {}
    }

    onClick(event) {
      const filter = event.target.closest('[data-mix-filter]');
      if (filter) {
        event.preventDefault();
        this.setFilter(filter.dataset.mixFilter);
        return;
      }
      const submit = event.target.closest('[data-submit]');
      if (submit) {
        event.preventDefault();
        this.submit(submit);
        return;
      }
      const action = event.target.closest('[data-add], [data-remove]');
      if (!action || this.busy || action.disabled) return;
      event.preventDefault();
      const card = action.closest('[data-product-card]');
      const id = action.dataset.variantId || card?.querySelector('[data-variant-select]').value;
      this.changeQuantity(id, action.hasAttribute('data-add') ? 1 : -1);
      if (card && action.hidden) {
        const nextFocus = card.querySelector('[data-stepper]').hidden ? card.querySelector('.hg-mix__choose') : card.querySelector('[data-stepper] [data-add]:not(:disabled)');
        nextFocus?.focus({ preventScroll: true });
      }
    }

    changeQuantity(id, delta) {
      if (this.busy) return;
      const variant = this.variants.get(String(id));
      if (!variant) return;
      const previousCount = this.count;
      const quantity = this.selection.get(String(id)) || 0;
      if (delta > 0 && (!variant.available || this.count >= 8 || quantity >= this.remainingStock(variant))) return;
      const nextQuantity = Math.max(0, quantity + delta);
      if (nextQuantity === quantity) return;
      if (nextQuantity) this.selection.set(String(id), nextQuantity);
      else this.selection.delete(String(id));
      clearTimeout(this.celebrationTimer);
      this.celebrating = 0;
      const crossed = [3, 5, 8].find(threshold => previousCount < threshold && this.count >= threshold);
      if (crossed) {
        this.celebrating = crossed;
        this.celebrationTimer = setTimeout(() => {
          this.celebrating = 0;
          this.renderProgress();
        }, 1100);
      }
      if (!this.uncertain) this.showError('');
      if (!this.prefillVariantId) this.prefillMessageKey = '';
      this.persist();
      this.render();
      const announcement = this.t(this.count === 1 ? 'selectedCountOne' : 'selectedCount', { count: this.count });
      this.setText('[data-announcement]', crossed ? this.progressMessage() : `${announcement} ${this.progressMessage()}`);
    }

    progressMessage() {
      if (this.celebrating) return this.discountsActive ? this.t('discountReached', { percent: this.rates[this.celebrating] }) : this.t(`ready${this.celebrating}`);
      if (this.count === 0) return this.t('startMessage');
      if (this.discountsActive) return this.count >= 8
        ? this.t('discountReached', { percent: this.rates[8] })
        : this.t('nextDiscount', { count: this.nextTarget - this.count, percent: this.rates[this.nextTarget] });
      if (this.count < 3) return this.t('moreToStart', { count: 3 - this.count });
      if (this.count >= 8) return this.t('maxMessage');
      return this.t('nextTier', { count: this.nextTarget - this.count, target: this.nextTarget });
    }

    renderProgress() {
      const target = this.celebrating || this.nextTarget;
      const progress = Math.min(100, this.count / target * 100);
      this.setText('[data-progress-message]', this.progressMessage());
      this.setText('[data-progress-counter]', `${this.count} / ${target}`);
      this.querySelectorAll('[data-progress-bar]').forEach(bar => {
        bar.style.setProperty('--mix-progress', `${progress}%`);
        if (bar.hasAttribute('role')) {
          bar.setAttribute('aria-valuemax', target);
          bar.setAttribute('aria-valuenow', Math.min(this.count, target));
          bar.setAttribute('aria-valuetext', this.progressMessage());
        }
      });
      this.querySelectorAll('[data-tier]').forEach(tier => {
        const threshold = Number(tier.dataset.tier);
        const achieved = this.count >= threshold;
        tier.classList.toggle('is-achieved', achieved);
        tier.classList.toggle('is-next', !achieved && this.nextTarget === threshold);
        tier.classList.toggle('hg-mix__tier--offer', this.offerAvailable);
        tier.querySelector('[data-tier-state]').textContent = achieved ? this.t('achieved') : this.nextTarget === threshold ? this.t('next') : '';
        const label = tier.querySelector('[data-tier-label]');
        label.classList.toggle('hg-mix__tier-discount', this.offerAvailable);
        label.textContent = this.offerAvailable ? this.t('discountLabel', { percent: this.rates[threshold] }) : this.t(`tier${threshold}`);
      });
      this.setText('[data-milestone-message]', this.currentTier ? this.t(`hint${this.currentTier}`) : this.t('startHint'));
      this.classList.toggle('is-celebrating', Boolean(this.celebrating));
    }

    renderCard(card) {
      const select = card.querySelector('[data-variant-select]');
      const variant = this.variants.get(select.value);
      if (!variant) return;
      const quantity = this.selection.get(variant.id) || 0;
      const available = variant.available && quantity < this.remainingStock(variant);
      const totalForProduct = [...this.selection].filter(([id]) => this.variants.get(id)?.card === card).reduce((sum, [, value]) => sum + value, 0);
      card.classList.toggle('is-selected', totalForProduct > 0);
      card.querySelector('[data-card-price]').textContent = this.money(variant.price);
      const compare = card.querySelector('[data-card-compare]');
      compare.textContent = variant.compare > variant.price ? this.money(variant.compare) : '';
      compare.hidden = !(variant.compare > variant.price);
      const unitPrice = card.querySelector('[data-unit-price]');
      unitPrice.hidden = !(variant.unitPrice > 0 && variant.unitReference);
      unitPrice.textContent = unitPrice.hidden ? '' : `${this.money(variant.unitPrice)} / ${variant.unitReference}`;
      const image = card.querySelector('.hg-mix__image');
      if (image && variant.image && image.dataset.variantId !== variant.id) {
        if (image.dataset.variantId) {
          image.src = variant.image;
          image.removeAttribute('srcset');
        }
        image.dataset.variantId = variant.id;
        image.alt = variant.imageAlt;
        let badge = card.querySelector('.hg-ai-badge');
        const marked = variant.imageAlt.startsWith('KI-Bild:');
        if (marked && !badge) {
          badge = document.createElement('span');
          badge.className = 'hg-ai-badge hg-ai-card-badge';
          badge.textContent = this.t('imageDisclosure');
          card.querySelector('.hg-mix__image-link').append(badge);
        }
        if (badge) badge.hidden = !marked;
      }
      card.querySelector('[data-card-badge]').hidden = totalForProduct === 0;
      card.querySelector('[data-card-badge]').textContent = this.t('selectedBadge', { count: totalForProduct });
      card.querySelector('[data-stepper]').hidden = quantity === 0;
      card.querySelector('.hg-mix__choose').hidden = quantity > 0;
      card.querySelector('[data-card-count]').textContent = String(quantity);
      card.querySelector('[data-choose-label]').textContent = variant.available ? this.t('choose') : this.t('soldOut');
      card.querySelector('[data-stock-note]').hidden = !variant.available || this.count >= 8 || quantity < this.remainingStock(variant);
      card.querySelectorAll('[data-add]').forEach(button => { button.disabled = this.busy || !available || this.count >= 8; });
      card.querySelectorAll('[data-remove]').forEach(button => { button.disabled = this.busy || quantity === 0; });
      select.disabled = this.busy;
    }

    renderSelection() {
      const list = this.querySelector('[data-selection-list]');
      const focused = document.activeElement;
      const focusId = focused?.closest('[data-selection-list]') ? focused.dataset.variantId : null;
      const focusAction = focused?.hasAttribute('data-add') ? 'data-add' : 'data-remove';
      const fragment = document.createDocumentFragment();
      this.selection.forEach((quantity, id) => {
        const variant = this.variants.get(id);
        const item = document.createElement('li');
        item.className = 'hg-mix__selection-item';
        if (variant.image) {
          const image = document.createElement('img');
          image.src = variant.image;
          image.alt = '';
          image.width = 52;
          image.height = 52;
          image.loading = 'lazy';
          item.append(image);
        }
        const info = document.createElement('div');
        info.className = 'hg-mix__selection-info';
        const title = document.createElement('a');
        title.href = variant.url;
        title.textContent = variant.title + (variant.variantTitle ? ` · ${variant.variantTitle}` : '');
        const price = document.createElement('span');
        price.className = 'hg-mix__selection-price';
        const original = variant.price * quantity;
        const discounted = this.discountedUnitPrice(variant) * quantity;
        if (discounted < original) {
          const compare = document.createElement('s');
          compare.textContent = this.money(original);
          compare.setAttribute('aria-label', this.t('originalPrice', { amount: this.money(original) }));
          price.append(compare);
        }
        const final = document.createElement('strong');
        final.textContent = this.money(discounted);
        price.append(final);
        info.append(title, price);
        const controls = document.createElement('div');
        controls.className = 'hg-mix__mini-stepper';
        [['remove', '−'], ['count', String(quantity)], ['add', '+']].forEach(([action, text]) => {
          const control = document.createElement(action === 'count' ? 'span' : 'button');
          control.textContent = text;
          if (action !== 'count') {
            control.type = 'button';
            control.setAttribute(`data-${action}`, '');
            control.dataset.variantId = id;
            control.setAttribute('aria-label', this.t(`${action}Aria`, { title: variant.title }));
            control.disabled = this.busy || (action === 'add' && (this.count >= 8 || quantity >= this.remainingStock(variant)));
          }
          controls.append(control);
        });
        item.append(info, controls);
        fragment.append(item);
      });
      list.replaceChildren(fragment);
      list.hidden = this.count === 0;
      this.querySelector('[data-empty-selection]').hidden = this.count !== 0;
      if (focusId) list.querySelector(`[${focusAction}][data-variant-id="${focusId}"]`)?.focus({ preventScroll: true });
    }

    render() {
      this.renderOffer();
      this.cards.forEach(card => this.renderCard(card));
      this.renderProgress();
      this.renderSelection();
      this.renderPrefillMessage();
      const originalTotal = this.total;
      const discountedTotal = this.discountedTotal;
      const savings = originalTotal - discountedTotal;
      this.setText('[data-total]', this.money(discountedTotal));
      this.querySelectorAll('[data-original-total]').forEach(original => {
        original.hidden = savings <= 0;
        original.textContent = savings > 0 ? this.money(originalTotal) : '';
        original.setAttribute('aria-label', this.t('originalPrice', { amount: this.money(originalTotal) }));
      });
      this.querySelectorAll('[data-savings]').forEach(element => {
        element.hidden = savings <= 0;
        element.textContent = savings > 0 ? this.t('savings', { percent: this.activeRate }) : '';
      });
      this.setText('[data-selection-count]', `${this.count} / 8`);
      this.setText('[data-mobile-count]', this.t(this.count === 1 ? 'selectedCountOne' : 'selectedCount', { count: this.count }));
      const active = this.discountsActive && this.currentTier > 0;
      this.querySelectorAll('[data-active-discount]').forEach(discount => {
        discount.hidden = !active;
        discount.textContent = active ? this.t('activeDiscount', { percent: this.rates[this.currentTier] }) : '';
      });
      this.querySelector('[data-discount-note]').hidden = !active;
      this.querySelectorAll('[data-submit]').forEach(button => {
        button.disabled = this.busy || this.prefillPending || (!this.uncertain && (this.count < 1 || this.count > 8));
        button.toggleAttribute('aria-busy', this.busy);
        button.textContent = this.busy ? this.t('adding') : this.uncertain ? this.t('reviewCart') : this.count === 0 ? this.t('startButton') : this.t('submit');
      });
    }

    showError(message) {
      const error = this.querySelector('[data-error]');
      error.hidden = !message;
      error.textContent = message;
      if (message) {
        this.setText('[data-announcement]', message);
        error.focus({ preventScroll: true });
        error.scrollIntoView({ block: 'nearest', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }
    }

    publishCart(cart, parsedState, activeElement) {
      const api = this.themeApi;
      if (cart && api?.pubsub?.PUB_SUB_EVENTS) api.pubsub.publish(api.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'hg-tea-mix', cart });
      if (parsedState) document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsedState } }));
      const drawer = document.querySelector('cart-drawer');
      if (api?.settings?.cartType !== 'page' && drawer?.show && cart) drawer.show(activeElement);
      else window.location.assign(this.cartUrl);
    }

    async submit(activeElement) {
      if (this.busy || this.prefillPending) return;
      if (this.uncertain) { window.location.assign(this.cartUrl); return; }
      if (this.count < 1 || this.count > 8) return;
      this.busy = true;
      this.cartGeneration += 1;
      clearTimeout(this.celebrationTimer);
      this.celebrating = 0;
      this.showError('');
      this.render();
      const requested = [...this.selection].map(([id, quantity]) => ({ id, quantity }));
      let before = null;
      let posted = false;
      let accepted = false;
      let rejected = false;
      try {
        before = await this.readCart();
        this.stockInCart = this.cartQuantities(before);
        const insufficient = requested.find(item => {
          const variant = this.variants.get(item.id);
          return !variant.available || item.quantity > this.remainingStock(variant);
        });
        if (insufficient) {
          const variant = this.variants.get(insufficient.id);
          throw new Error(this.t('stockError', { title: variant.title, count: this.remainingStock(variant) }));
        }
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));
        posted = true;
        const response = await fetch(this.addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: requested, sections: [...new Set(sections)].slice(0, 5), sections_url: window.location.pathname })
        });
        const parsedState = await response.json();
        if (!response.ok || parsedState.status) {
          rejected = true;
          const message = typeof parsedState.description === 'string' ? parsedState.description : this.t('error');
          document.dispatchEvent(new CustomEvent('ajaxProduct:error', { detail: { errorMessage: message } }));
          throw new Error(message);
        }
        accepted = true;
        const sectionsComplete = [...new Set(sections)].every(id => typeof parsedState.sections?.[id] === 'string' && parsedState.sections[id].trim() !== '');
        if (!sectionsComplete) {
          this.selection.clear();
          this.persist();
          window.location.assign(this.cartUrl);
          return;
        }
        let cart;
        try {
          cart = this.themeApi?.utils?.cartStateFromResponse ? await this.themeApi.utils.cartStateFromResponse(parsedState) : await this.readCart();
          cart.sections = parsedState.sections;
        } catch (_) {
          this.selection.clear();
          this.persist();
          window.location.assign(this.cartUrl);
          return;
        }
        this.stockInCart = this.cartQuantities(cart);
        this.selection.clear();
        this.persist();
        this.render();
        this.setText('[data-announcement]', this.t('added'));
        this.publishCart(cart, parsedState, activeElement);
      } catch (error) {
        let message = error.message || this.t('error');
        if (posted && !accepted) {
          if (!rejected) this.uncertain = true;
          try {
            const cart = await this.readCart();
            const afterQuantities = this.cartQuantities(cart);
            const beforeQuantities = this.cartQuantities(before || {});
            const changed = requested.some(item => (afterQuantities.get(item.id) || 0) !== (beforeQuantities.get(item.id) || 0));
            this.stockInCart = afterQuantities;
            if (changed) this.uncertain = true;
            if (this.themeApi?.pubsub?.PUB_SUB_EVENTS) this.themeApi.pubsub.publish(this.themeApi.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'hg-tea-mix', cart });
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
          } catch (_) { this.uncertain = true; }
        }
        if (accepted) {
          window.location.assign(this.cartUrl);
          return;
        }
        if (this.uncertain) message = this.t('uncertain');
        this.showError(message);
      } finally {
        this.busy = false;
        this.render();
      }
    }
  });
}




